/**
 * Vigencia por fechas de las promociones THCC (hora de Ciudad de México, sin horario de verano).
 *
 * El servidor ya decide qué se pinta (snippets/thcc-vigencia.liquid); este módulo lo vuelve a evaluar en el
 * navegador porque la página puede venir de caché, y es quien aplica la fecha de prueba.
 *
 * Fecha de prueba: `?thcc_fecha=AAAA-MM-DD` en la URL. Solo cuenta si el elemento (o un ancestro) trae
 * `data-prueba="true"`, que el tema pinta únicamente cuando theme.role no es "main". En el tema en vivo la
 * URL se ignora, así un cliente no puede forzar fechas.
 *
 * Contrato de marcado:
 * - `[data-thcc-vigencia-raiz]`: contenedor con las plantillas de la insignia (data-t-*) y `data-prueba`.
 *   Cada `[data-thcc-vigencia][data-desde][data-hasta]` hijo se oculta si está fuera de fecha, y su
 *   `[data-thcc-insignia]` recibe el texto de vigencia. `data-edicion="true"` (editor de temas) no filtra nada.
 * - `[data-thcc-envio-899]`: el texto base trae "$999"; el valor del atributo es el texto con "$899" que se usa
 *   del 24 oct al 2 nov 2026 (igual que snippets/thcc-cart-umbrales.liquid). Lo usa snippets/thcc-envio-gratis-monto.liquid.
 * - `[data-thcc-envio-estado="999"|"899"]`: variantes completas de un bloque (escalera de la ficha). Se muestra
 *   la que toca por la fecha y se oculta la otra con `hidden`.
 */

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;
const DIA_MS = 86400000;

/** Ventana del envío gratis desde $899 (inclusive). Mantener igual que snippets/thcc-cart-umbrales.liquid. */
export const ENVIO_899 = { desde: '2026-10-24', hasta: '2026-11-02' };

/**
 * @param {unknown} valor
 * @returns {string} La fecha AAAA-MM-DD, o '' si no es una fecha válida.
 */
export function limpiarFecha(valor) {
  if (typeof valor !== 'string') return '';
  const texto = valor.trim();
  if (!FECHA_RE.test(texto)) return '';
  const hora = Date.parse(`${texto}T00:00:00Z`);
  return Number.isNaN(hora) ? '' : texto;
}

/** @param {string} fecha AAAA-MM-DD */
function diaNumero(fecha) {
  return Date.parse(`${fecha}T00:00:00Z`) / DIA_MS;
}

let avisoMostrado = false;

/** Etiqueta fija para que quien prueba sepa que la fecha no es la real. Solo existe fuera del tema en vivo. */
function avisarFechaSimulada(fecha) {
  if (avisoMostrado || !document.body) return;
  avisoMostrado = true;
  const aviso = document.createElement('p');
  aviso.textContent = `Fecha simulada: ${fecha}`;
  aviso.setAttribute('role', 'status');
  Object.assign(aviso.style, {
    position: 'fixed',
    insetInlineStart: '12px',
    insetBlockEnd: '12px',
    zIndex: '2147483000',
    margin: '0',
    padding: '6px 12px',
    borderRadius: '999px',
    background: '#0b132b',
    color: '#ffd6ec',
    font: '600 12px/1.3 system-ui, sans-serif',
    pointerEvents: 'none',
  });
  document.body.append(aviso);
  console.info(`[THCC] Fecha simulada por ?thcc_fecha: ${fecha}`);
}

/**
 * Hoy en Ciudad de México como AAAA-MM-DD.
 * @param {boolean} [permitirPrueba] Acepta `?thcc_fecha=` (solo fuera del tema en vivo).
 * @returns {{ fecha: string, simulada: boolean }}
 */
export function hoyCDMX(permitirPrueba = false) {
  if (permitirPrueba) {
    const pedida = limpiarFecha(new URLSearchParams(window.location.search).get('thcc_fecha'));
    if (pedida) {
      avisarFechaSimulada(pedida);
      return { fecha: pedida, simulada: true };
    }
  }
  const partes = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(new Date())
      .map((parte) => [parte.type, parte.value])
  );
  return { fecha: `${partes.year}-${partes.month}-${partes.day}`, simulada: false };
}

/**
 * Una fecha vacía o inválida no limita ese extremo.
 * @param {string | undefined} desde
 * @param {string | undefined} hasta
 * @param {string} hoy AAAA-MM-DD
 */
export function vigente(desde, hasta, hoy) {
  const d = limpiarFecha(desde);
  const h = limpiarFecha(hasta);
  if (d && hoy < d) return false;
  if (h && hoy > h) return false;
  return true;
}

/**
 * @param {string} hasta AAAA-MM-DD
 * @param {string} hoy AAAA-MM-DD
 * @returns {number} Días que faltan para el último día (0 = hoy es el último).
 */
export function diasRestantes(hasta, hoy) {
  return diaNumero(hasta) - diaNumero(hoy);
}

/**
 * @typedef {object} Plantillas
 * @property {string[]} meses Doce abreviaturas, de enero a diciembre.
 * @property {string} rango "Del {desde} al {hasta}"
 * @property {string} hasta "Hasta el {hasta}"
 * @property {string} unico "Solo el {hasta}"
 * @property {string} ultimo "Último día"
 * @property {string} dias "Termina en {n} días"
 * @property {string} dia "Termina en 1 día"
 */

/**
 * @param {DOMStringMap} datos Dataset del contenedor con las plantillas (data-t-*).
 * @returns {Plantillas}
 */
export function plantillasDe(datos) {
  return {
    meses: (datos.tMeses || 'ene,feb,mar,abr,may,jun,jul,ago,sep,oct,nov,dic').split(',').map((m) => m.trim()),
    rango: datos.tRango || 'Del {desde} al {hasta}',
    hasta: datos.tHasta || 'Hasta el {hasta}',
    unico: datos.tUnico || 'Solo el {hasta}',
    ultimo: datos.tUltimo || 'Último día',
    dias: datos.tDias || 'Termina en {n} días',
    dia: datos.tDia || 'Termina en 1 día',
  };
}

/**
 * Texto de la insignia de una promoción. Vacío si no tiene fecha final (nada "termina").
 * @param {string | undefined} desde
 * @param {string | undefined} hasta
 * @param {string} hoy AAAA-MM-DD
 * @param {Plantillas} t
 * @returns {{ texto: string, urgente: boolean }}
 */
export function textoInsignia(desde, hasta, hoy, t) {
  const d = limpiarFecha(desde);
  const h = limpiarFecha(hasta);
  if (!h) return { texto: '', urgente: false };

  const restan = diasRestantes(h, hoy);
  if (restan < 0) return { texto: '', urgente: false };
  if (restan === 0) return { texto: t.ultimo, urgente: true };
  if (restan <= 3) return { texto: restan === 1 ? t.dia : t.dias.replace('{n}', String(restan)), urgente: true };

  /** @param {string} fecha */
  const corta = (fecha) => `${Number(fecha.slice(8, 10))} ${t.meses[Number(fecha.slice(5, 7)) - 1] ?? ''}`;
  if (!d) return { texto: t.hasta.replace('{hasta}', corta(h)), urgente: false };
  if (d === h) return { texto: t.unico.replace('{hasta}', corta(h)), urgente: false };
  return { texto: t.rango.replace('{desde}', corta(d)).replace('{hasta}', corta(h)), urgente: false };
}

/**
 * Pone el texto en la insignia: oculta la insignia si no hay texto.
 * @param {Element} insignia
 * @param {{ texto: string, urgente: boolean }} resultado
 */
export function pintarInsignia(insignia, resultado) {
  const destino = insignia.querySelector('[data-thcc-insignia-texto]') ?? insignia;
  destino.textContent = resultado.texto;
  insignia.toggleAttribute('hidden', !resultado.texto);
  insignia.toggleAttribute('data-urgente', resultado.urgente);
}

/** @param {Element} el */
function permitePrueba(el) {
  return el.closest('[data-prueba="true"]') !== null;
}

/** Contenedores genéricos (tarjetas de promociones): oculta lo vencido y pone la insignia. */
function iniciarRaices() {
  for (const raiz of document.querySelectorAll('[data-thcc-vigencia-raiz]')) {
    if (!(raiz instanceof HTMLElement) || raiz.dataset.edicion === 'true') continue;
    const { fecha } = hoyCDMX(permitePrueba(raiz));
    const plantillas = plantillasDe(raiz.dataset);
    for (const item of raiz.querySelectorAll('[data-thcc-vigencia]')) {
      if (!(item instanceof HTMLElement)) continue;
      const activo = vigente(item.dataset.desde, item.dataset.hasta, fecha);
      item.hidden = !activo;
      item.removeAttribute('data-fuera');
      const insignia = item.querySelector('[data-thcc-insignia]');
      if (insignia && activo) pintarInsignia(insignia, textoInsignia(item.dataset.desde, item.dataset.hasta, fecha, plantillas));
    }
  }
}

/** Textos con "$999" que pasan a "$899" durante la ventana del envío gratis desde $899. */
function iniciarEnvio899() {
  for (const el of document.querySelectorAll('[data-thcc-envio-899]')) {
    if (!(el instanceof HTMLElement)) continue;
    // dataset no convierte "-899" (guion antes de un dígito): se lee el atributo tal cual.
    const promo = el.getAttribute('data-thcc-envio-899');
    if (!promo) continue;
    el.dataset.thccBase ??= el.textContent ?? '';
    const { fecha } = hoyCDMX(permitePrueba(el));
    const activa = vigente(ENVIO_899.desde, ENVIO_899.hasta, fecha);
    const texto = activa ? promo : el.dataset.thccBase;
    if (el.textContent !== texto) el.textContent = texto;
  }
}

/** Variantes completas con el envío gratis en $999 o en $899: deja visible solo la que toca hoy. */
function iniciarEnvioEstados() {
  for (const el of document.querySelectorAll('[data-thcc-envio-estado]')) {
    if (!(el instanceof HTMLElement)) continue;
    const { fecha } = hoyCDMX(permitePrueba(el));
    const activa = vigente(ENVIO_899.desde, ENVIO_899.hasta, fecha);
    el.hidden = (el.dataset.thccEnvioEstado === '899') !== activa;
  }
}

function iniciar() {
  iniciarRaices();
  iniciarEnvio899();
  iniciarEnvioEstados();
}

iniciar();
document.addEventListener('shopify:section:load', iniciar);
